import type { Document } from "@/types/documents";
import { reportDocumentTableFailure } from "./documentTableFailure";
import type { DocumentWithTags } from "./documentTableTypes";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableCompare } from "./useTableCompare";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTableDocumentKinds } from "./useTableDocumentKinds";
import type { useTableSigning } from "./useTableSigning";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  | "store"
  | "onAskFranklin"
  | "hostAction"
  | "selectedFolderIds"
  | "onFolderSelect"
  | "onBulkDeleteComplete"
> &
  Pick<ReturnType<typeof useTableDocumentKinds>, "displayDocuments"> &
  Pick<
    ReturnType<typeof useTableDialogState>,
    "versionDocsCache" | "setCompareDocuments" | "setCompareOpen"
  > &
  Pick<ReturnType<typeof useTableCompare>, "selectedPdfs"> &
  Pick<ReturnType<typeof useTableSigning>, "allDocuments">;
export function createTableBulkActions(options: Options) {
  const {
    store,
    displayDocuments,
    versionDocsCache,
    onAskFranklin,
    hostAction,
    selectedPdfs,
    setCompareDocuments,
    setCompareOpen,
    selectedFolderIds,
    allDocuments,
    onFolderSelect,
    onBulkDeleteComplete,
  } = options;
  // The full list of selected docs for "Ask Franklin" and a host action,
  // including older versions from versionDocsCache that are not in the MST
  // store or the `documents` prop but are still selectable via their
  // inline-expansion rows.
  const selectedActionDocs = (): Array<{ id: string; filename: string }> => {
    if (!store) return [];
    const out: Array<{ id: string; filename: string }> = [];
    const seen = new Set<string>();
    for (const doc of displayDocuments) {
      if (store.isSelected(doc.id) && !seen.has(doc.id)) {
        seen.add(doc.id);
        out.push({ id: doc.id, filename: doc.filename });
      }
    }
    for (const entry of versionDocsCache.values()) {
      for (const v of entry.docs) {
        if (store.isSelected(v.id) && !seen.has(v.id)) {
          seen.add(v.id);
          out.push({ id: v.id, filename: v.filename });
        }
      }
    }
    return out;
  };
  const handleAskFranklinClick = onAskFranklin
    ? () => {
        if (!store) return;
        onAskFranklin(selectedActionDocs());
      }
    : undefined;
  const hostActionOnSelection = hostAction
    ? {
        label: hostAction.label,
        icon: hostAction.icon,
        onClick: () => hostAction.onRun(selectedActionDocs()),
      }
    : undefined;

  // Handle PDF comparison (when 2 PDFs are selected)
  const handleComparePdfs = () => {
    if (selectedPdfs.length !== 2) return;

    // Convert DocumentWithTags to Document type for PdfCompareDialog
    const convertToDocument = (doc: DocumentWithTags): Document => {
      return {
        id: doc.id,
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: doc.building_id,
        content_type: doc.content_type,
        size: doc.size,
        created_at: doc.created_at,
        parent_document_id: doc.parent_document_id,
        version: doc.version ?? undefined,
        created_by_name: doc.created_by_name ?? undefined,
      };
    };

    try {
      const docA = convertToDocument(selectedPdfs[0]);
      const docB = convertToDocument(selectedPdfs[1]);
      setCompareDocuments({ docA, docB });
      setCompareOpen(true);
    } catch (error) {
      reportDocumentTableFailure("comparison_prepare", error);
    }
  };

  // Bulk delete of the current selection. Shared by the floating BulkActionBar
  // and the row right-click context menu so both behave identically.
  const runBulkDelete = () => {
    store
      // allDocuments as extraRows: on project/share surfaces the rendered rows
      // never enter the MST map, and event logging needs their filenames.
      ?.bulkMoveToTrash(selectedFolderIds.length > 0 ? selectedFolderIds : undefined, allDocuments)
      .then((didTrash) => {
        // Abort paths (failed selection lookup, failed RPC) resolve false, not
        // reject — clearing the folder selection there would lose half the
        // user's selection right after an error toast said nothing changed.
        if (!didTrash) return;
        // Clear folder selections and refresh folder tree after trash
        selectedFolderIds.forEach((id) => onFolderSelect?.(id, false));
        // Always notify the parent so consumers (e.g. /projects) can refresh
        // derived state like folder document counts even when only documents
        // (not folders) were in the selection.
        onBulkDeleteComplete?.();
      })
      .catch((error) => {
        reportDocumentTableFailure("bulk_trash", error);
      });
  };

  // Bulk download of the current selection. Shared by the toolbar and the
  // right-click context menu.
  const runBulkDownload = () => {
    if (!store) return;
    // Resolve through the store, with allDocuments as extraRows — same contract
    // as runBulkDelete. Pre-filtering allDocuments here instead would silently
    // drop every selected doc this surface doesn't render (another folder's
    // rows on /projects, the folder-checkbox cascade, timeline rows).
    // allDocuments (not displayDocuments) because displayDocuments excludes
    // container files (.zip/.asice/.bdoc/.ddoc), so a selected container would
    // otherwise be dropped and bulkDownload would report "No selection"
    // despite a checked row.
    store
      .bulkDownload(
        undefined,
        selectedFolderIds.length > 0 ? selectedFolderIds : undefined,
        allDocuments
      )
      .then((didDownload) => {
        // Same contract as runBulkDelete: false means nothing was downloaded,
        // so the selection stays put for a retry.
        if (!didDownload) return;
        if (selectedFolderIds.length > 0) {
          selectedFolderIds.forEach((id) => onFolderSelect?.(id, false));
        }
      })
      .catch((error) => {
        reportDocumentTableFailure("bulk_download", error);
      });
  };
  return {
    runBulkDownload,
    handleAskFranklinClick,
    hostActionOnSelection,
    handleComparePdfs,
    runBulkDelete,
  };
}
